// Configuration for supported languages in ambassador signup
// Each language has a translation key and a value in English

import { useI18n } from 'vue-i18n';

export interface LanguageOption {
  label: string; // Translation key or translated name
  value: string; // English name for backend
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  // Major world languages (A-Z by English name)
  { label: 'Arabic', value: 'arabic' },
  { label: 'Bengali', value: 'bengali' },
  { label: 'Bulgarian', value: 'bulgarian' },
  { label: 'Cantonese', value: 'cantonese' },
  { label: 'Catalan', value: 'catalan' },
  { label: 'Chinese', value: 'chinese' },
  { label: 'Croatian', value: 'croatian' },
  { label: 'Czech', value: 'czech' },
  { label: 'Danish', value: 'danish' },
  { label: 'Dutch', value: 'dutch' },
  { label: 'English', value: 'english' },
  { label: 'Estonian', value: 'estonian' },
  { label: 'Finnish', value: 'finnish' },
  { label: 'French', value: 'french' },
  { label: 'German', value: 'german' },
  { label: 'Greek', value: 'greek' },
  { label: 'Gujarati', value: 'gujarati' },
  { label: 'Hebrew', value: 'hebrew' },
  { label: 'Hindi', value: 'hindi' },
  { label: 'Hungarian', value: 'hungarian' },
  { label: 'Icelandic', value: 'icelandic' },
  { label: 'Indonesian', value: 'indonesian' },
  { label: 'Italian', value: 'italian' },
  { label: 'Japanese', value: 'japanese' },
  { label: 'Korean', value: 'korean' },
  { label: 'Latvian', value: 'latvian' },
  { label: 'Lithuanian', value: 'lithuanian' },
  { label: 'Malay', value: 'malay' },
  { label: 'Malayalam', value: 'malayalam' },
  { label: 'Marathi', value: 'marathi' },
  { label: 'Norwegian', value: 'norwegian' },
  { label: 'Persian', value: 'persian' },
  { label: 'Polish', value: 'polish' },
  { label: 'Portuguese', value: 'portuguese' },
  { label: 'Punjabi', value: 'punjabi' },
  { label: 'Romanian', value: 'romanian' },
  { label: 'Russian', value: 'russian' },
  { label: 'Serbian', value: 'serbian' },
  { label: 'Slovak', value: 'slovak' },
  { label: 'Slovenian', value: 'slovenian' },
  { label: 'Spanish', value: 'spanish' },
  { label: 'Swahili', value: 'swahili' },
  { label: 'Swedish', value: 'swedish' },
  { label: 'Tamil', value: 'tamil' },
  { label: 'Telugu', value: 'telugu' },
  { label: 'Thai', value: 'thai' },
  { label: 'Turkish', value: 'turkish' },
  { label: 'Ukrainian', value: 'ukrainian' },
  { label: 'Urdu', value: 'urdu' },
  { label: 'Vietnamese', value: 'vietnamese' },

  // Additional important regional languages
  { label: 'Afrikaans', value: 'afrikaans' },
  { label: 'Amharic', value: 'amharic' },
  { label: 'Armenian', value: 'armenian' },
  { label: 'Azerbaijani', value: 'azerbaijani' },
  { label: 'Basque', value: 'basque' },
  { label: 'Belarusian', value: 'belarusian' },
  { label: 'Bosnian', value: 'bosnian' },
  { label: 'Burmese', value: 'burmese' },
  { label: 'Esperanto', value: 'esperanto' },
  { label: 'Georgian', value: 'georgian' },
  { label: 'Irish', value: 'irish' },
  { label: 'Kazakh', value: 'kazakh' },
  { label: 'Khmer', value: 'khmer' },
  { label: 'Kurdish', value: 'kurdish' },
  { label: 'Lao', value: 'lao' },
  { label: 'Latin', value: 'latin' },
  { label: 'Luxembourgish', value: 'luxembourgish' },
  { label: 'Macedonian', value: 'macedonian' },
  { label: 'Malagasy', value: 'malagasy' },
  { label: 'Maltese', value: 'maltese' },
  { label: 'Mongolian', value: 'mongolian' },
  { label: 'Nepali', value: 'nepali' },
  { label: 'Pashto', value: 'pashto' },
  { label: 'Sinhala', value: 'sinhala' },
  { label: 'Tagalog', value: 'tagalog' },
  { label: 'Tajik', value: 'tajik' },
  { label: 'Tatar', value: 'tatar' },
  { label: 'Turkmen', value: 'turkmen' },
  { label: 'Uzbek', value: 'uzbek' },
  { label: 'Welsh', value: 'welsh' },
  { label: 'Yiddish', value: 'yiddish' },
  { label: 'Yoruba', value: 'yoruba' },
  { label: 'Zulu', value: 'zulu' },
];

// Composable for language options
export function useLanguageOptions() {
  const { t } = useI18n();

  // Helper function to get language options sorted by translated label
  function getSortedLanguageOptions(): LanguageOption[] {
    return [...SUPPORTED_LANGUAGES]
      .map((lang) => ({
        ...lang,
        label: t(`languages.${lang.value}`),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  // Helper function to find language by value
  function findLanguageByValue(value: string): LanguageOption | undefined {
    const lang = SUPPORTED_LANGUAGES.find((lang) => lang.value === value);
    return lang ? { ...lang, label: t(`languages.${lang.value}`) } : undefined;
  }

  // Helper function to find language by label (using translation key)
  function findLanguageByLabel(label: string): LanguageOption | undefined {
    const lang = SUPPORTED_LANGUAGES.find(
      (lang) => t(`languages.${lang.value}`) === label
    );
    return lang ? { ...lang, label: t(`languages.${lang.value}`) } : undefined;
  }

  return {
    getSortedLanguageOptions,
    findLanguageByValue,
    findLanguageByLabel,
  };
}

// Legacy functions for backward compatibility - these will use fallback English labels
export function getSortedLanguageOptions(): LanguageOption[] {
  return [...SUPPORTED_LANGUAGES].sort((a, b) =>
    a.label.localeCompare(b.label)
  );
}

export function findLanguageByValue(value: string): LanguageOption | undefined {
  return SUPPORTED_LANGUAGES.find((lang) => lang.value === value);
}

export function findLanguageByLabel(label: string): LanguageOption | undefined {
  return SUPPORTED_LANGUAGES.find((lang) => lang.label === label);
}
